<template>
  <div class="extras-mg-bateria" :title="resumen">
    <template v-if="segmentos.length">
      <div
        v-for="seg in segmentos"
        :key="seg.etiqueta"
        class="extras-mg-bateria__tramo"
        :class="seg.clase"
        :style="{ width: seg.porcentaje + '%' }"
      >
        <span v-if="seg.porcentaje >= 14" class="extras-mg-bateria__texto">
          {{ Math.round(seg.porcentaje) }}%
        </span>
      </div>
    </template>
    <div v-else class="extras-mg-bateria__vacia">—</div>
  </div>
</template>

<script>
/**
 * La "bateria" de Monday: una barra partida en tramos de color que dice, de un
 * vistazo, como esta repartida una columna de seleccion.
 *
 * Se apoya en la agregacion `distribution` que Baserow ya calcula en el
 * servidor (cuantas filas por valor); lo unico que agrega esto es dibujarla.
 * Los colores salen de las opciones del propio campo, asi que cambiar el color
 * de una opcion recolorea la barra sola.
 *
 * Los porcentajes se pintan dentro del tramo solo si caben (14% de ancho); si
 * no, la barra se llena de numeros ilegibles. El detalle completo esta en el
 * title, al pasar el raton.
 */
export default {
  name: 'BateriaAggregation',
  props: {
    value: { type: [Array, Number, String, Object], required: false, default: null },
    field: { type: Object, required: true },
    fieldType: { type: Object, required: false, default: null },
    aggregationType: { type: Object, required: false, default: null },
    rowCount: { type: Number, required: false, default: 0 },
  },
  computed: {
    /**
     * `distribution` llega como [[valor, conteo], …]. Cuando aun no hay
     * respuesta puede venir un numero o null: entonces no hay nada que dibujar.
     */
    pares() {
      return Array.isArray(this.value) ? this.value : []
    },
    total() {
      return this.pares.reduce((suma, par) => suma + (Number(par[1]) || 0), 0)
    },
    opciones() {
      const porValor = {}
      for (const opcion of this.field.select_options || []) {
        porValor[opcion.value] = opcion.color
      }
      return porValor
    },
    segmentos() {
      if (!this.total) {
        return []
      }
      return this.pares
        .map(([etiqueta, conteo]) => {
          const color = this.opciones[etiqueta]
          return {
            etiqueta: String(etiqueta ?? ''),
            conteo: Number(conteo) || 0,
            porcentaje: ((Number(conteo) || 0) / this.total) * 100,
            // Sin color (texto, numeros, booleanos) se usa el gris neutro, que
            // es lo que hace Monday con las columnas que no son de estado.
            clase: color
              ? `background-color--${color}`
              : 'extras-mg-bateria__tramo--neutro',
          }
        })
        .sort((a, b) => b.conteo - a.conteo)
    },
    resumen() {
      return this.segmentos
        .map(
          (s) =>
            `${s.etiqueta || '(vacío)'}: ${s.conteo} (${Math.round(
              s.porcentaje
            )}%)`
        )
        .join(' · ')
    },
  },
}
</script>

<style lang="scss">
/* Sin colores fijos: los tramos heredan las clases de color de Baserow, que la
   piel ya mapea a la paleta de Monday. */
.extras-mg-bateria {
  display: flex;
  width: 100%;
  height: 20px;
  border-radius: 3px;
  overflow: hidden;
}

.extras-mg-bateria__tramo {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2px;
  transition: width 0.2s ease;
}

.extras-mg-bateria__tramo--neutro {
  background: color-mix(in srgb, currentColor 25%, transparent);
}

.extras-mg-bateria__texto {
  font-size: 11px;
  line-height: 1;
  color: inherit;
  opacity: 0.9;
}

.extras-mg-bateria__vacia {
  font-size: 12px;
  opacity: 0.5;
  width: 100%;
  text-align: center;
}
</style>
